import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, User, ArrowRight, Tag } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SEOHead } from '@/components/SEOHead';

interface BlogPost {
  id: string;
  titulo: string;
  slug: string;
  resumo: string;
  imagemCapa: string | null;
  tempoLeitura: number;
  publicadoEm: string;
  visualizacoes: number;
  geradoPorIA: boolean;
  categoria: {
    id: string;
    nome: string;
    slug: string;
    cor: string;
  };
  autor: {
    id: string;
    nome: string;
  };
  tags: {
    id: string;
    nome: string;
    slug: string;
  }[];
}

interface Categoria {
  id: string;
  nome: string;
  slug: string;
  cor: string;
  _count: {
    posts: number;
  };
}

export default function BlogList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState(searchParams.get('busca') || '');
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
  });

  const categoriaAtiva = searchParams.get('categoria') || '';

  useEffect(() => {
    carregarCategorias();
  }, []);

  useEffect(() => {
    carregarPosts();
  }, [categoriaAtiva, searchParams.get('page')]);

  const carregarCategorias = async () => {
    try {
      const { data } = await api.get('/blog/categorias');
      setCategorias(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const carregarPosts = async () => {
    try {
      setLoading(true);
      const page = searchParams.get('page') || '1';
      const params: any = { page, limit: 9 };

      if (categoriaAtiva) params.categoria = categoriaAtiva;
      if (searchParams.get('busca')) params.busca = searchParams.get('busca');

      const { data } = await api.get('/blog/posts', { params });
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBusca = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (busca) {
      newParams.set('busca', busca);
    } else {
      newParams.delete('busca');
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const handleCategoriaChange = (slug: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (slug === categoriaAtiva) {
      newParams.delete('categoria');
    } else {
      newParams.set('categoria', slug);
    }
    newParams.delete('page');
    setSearchParams(newParams);
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Blog - Impressão 3D, Maker e Tecnologia | Fort3D"
        description="Artigos sobre impressão 3D, prototipagem rápida, acessórios gamer e cultura maker. Guia completo com dicas, tutoriais e novidades."
        keywords="blog impressão 3D, prototipagem rápida, maker, cultura maker, acessórios gamer, tutoriais 3D"
        url="https://fort3d.com.br/blog"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-[#FF6B35] to-[#00D9FF] bg-clip-text text-transparent">
                Blog Fort3D
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              Artigos, tutoriais e guias sobre impressão 3D, prototipagem rápida 
              e cultura maker. Aprenda com conteúdo prático e atualizado.
            </p>

            {/* Barra de busca */}
            <form onSubmit={handleBusca} className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar artigos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-12 pr-4 py-3 w-full bg-white/10 border-white/20 text-white placeholder:text-gray-400 rounded-full"
              />
              <Button
                type="submit"
                variant="nuvem"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full"
              >
                Buscar
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categorias */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24">
              <h3 className="text-lg font-semibold mb-4 text-[#1a1a2e]">
                Categorias
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleCategoriaChange('')}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    categoriaAtiva === ''
                      ? 'bg-[#FF6B35] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas as Categorias
                </button>
                {categorias.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoriaChange(cat.slug)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      categoriaAtiva === cat.slug
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={{
                      backgroundColor: categoriaAtiva === cat.slug ? cat.cor : undefined,
                    }}
                  >
                    <span>{cat.nome}</span>
                    <span className="text-sm opacity-75">({cat._count.posts})</span>
                  </button>
                ))}
              </div>

              {/* AdSense Sidebar */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 text-center mb-2">Publicidade</p>
                <div className="bg-gray-200 border-2 border-dashed border-gray-300 rounded-lg h-60 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">AdSense</span>
                </div>
              </div>
            </div>
          </aside>

          {/* Lista de Posts */}
          <main className="flex-1">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  Nenhum artigo encontrado.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchParams({});
                    setBusca('');
                  }}
                  className="mt-4"
                >
                  Limpar filtros
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-8">
                  {posts.map((post, index) => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="flex flex-col md:flex-row">
                          {/* Imagem */}
                          <div className="md:w-1/3 h-48 md:h-auto relative">
                            {post.imagemCapa ? (
                              <img
                                src={post.imagemCapa}
                                alt={post.titulo}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#FF6B35] to-[#00D9FF] flex items-center justify-center">
                                <span className="text-white text-4xl font-bold">
                                  {post.titulo.charAt(0)}
                                </span>
                              </div>
                            )}
                            <span
                              className="absolute top-4 left-4 px-3 py-1 rounded-full text-white text-xs font-medium"
                              style={{ backgroundColor: post.categoria.cor }}
                            >
                              {post.categoria.nome}
                            </span>
                            {post.geradoPorIA && (
                              <span className="absolute top-4 right-4 px-2 py-1 bg-purple-500 text-white text-xs rounded-full">
                                IA
                              </span>
                            )}
                          </div>

                          {/* Conteúdo */}
                          <div className="p-6 md:w-2/3 flex flex-col">
                            <h2 className="text-xl md:text-2xl font-bold text-[#1a1a2e] mb-3 hover:text-[#FF6B35] transition-colors">
                              {post.titulo}
                            </h2>
                            <p className="text-gray-600 mb-4 line-clamp-2">
                              {post.resumo}
                            </p>

                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatarData(post.publicadoEm)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {post.tempoLeitura} min
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {post.autor.nome}
                              </span>
                            </div>

                            {post.tags.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-4">
                                {post.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                                  >
                                    <Tag className="w-3 h-3" />
                                    {tag.nome}
                                  </span>
                                ))}
                              </div>
                            )}

                            <div className="mt-auto flex items-center gap-2 text-[#FF6B35] font-medium">
                              <span>Ler artigo</span>
                              <ArrowRight className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>

                {/* Paginação */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => {
                            const newParams = new URLSearchParams(searchParams);
                            newParams.set('page', page.toString());
                            setSearchParams(newParams);
                          }}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                            pagination.page === page
                              ? 'bg-[#FF6B35] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
